import { createHash, randomUUID } from "node:crypto";
import { isIP } from "node:net";
import {
  createUIMessageStream,
  createUIMessageStreamResponse,
  type UIMessage,
} from "ai";
import {
  CHAT_CONVERSATION_ID_HEADER,
  createChatApiPaths,
  guestChatMessageRequestSchema,
  guestChatSessionResponseSchema,
  guestChatSubmissionStatusSchema,
  guestChatSubmissionIdSchema,
  guestChatHistoryResponseSchema,
  chatMessagesResponseSchema,
  deleteChatSessionResponseSchema,
  guestInterfaceType,
  getGuestSourceCards,
} from "@brains/contracts/chat";
import { askedBeforeSchema } from "@brains/contracts";
import type {
  InterfacePluginContext,
  WebRouteDefinition,
} from "@brains/plugins";
import { z } from "@brains/utils/zod";
import { deferred } from "@brains/utils/deferred";
import {
  GuestVisitorStore,
  canAccessGuestConversation,
  type GuestVisitor,
} from "./guest-access";
import { GuestAdmission } from "./guest-admission";
import type {
  GuestAdmissionDenial,
  GuestExecutionLease,
} from "./guest-admission-state";
import {
  GuestUsageRecord,
  type GuestUsageDenialReason,
  type GuestUsageHealth,
} from "./guest-usage-record";

/** The route's own refusal of a question, by its status; never its body. */
function refusal(status: number): GuestUsageDenialReason {
  switch (status) {
    case 403:
      return "forbidden";
    case 405:
      return "method";
    case 415:
      return "media-type";
    case 413:
      return "oversized";
    case 404:
      return "not-found";
    case 400:
      return "invalid-request";
    default:
      return "closed";
  }
}

import {
  guestPolicySchema,
  guestRequestOrigin,
  type GuestPolicy,
  type EnabledGuestPolicy,
} from "./guest-policy";
import type { WebChatConversation } from "./conversation-access";
import { writeTextPart } from "./stream-writer";

import type { AskContent } from "@brains/contracts";

export interface GuestHttpOptions {
  /** Bounded public authored copy, not execution configuration. */
  presentation?: () => Promise<AskContent | undefined>;
  /** What the brain's public work is about: the subjects a question is screened against. */
  subjects?: () => Promise<string[]>;
  /** Trusted host readiness, never browser configuration. Absent means closed.
   * Tests can supply a mocked runtime; production must verify accounting and
   * source-work prerequisites before supplying a positive readiness check.
   */
  ready?: () => boolean;
  now?: () => number;
  /** Runtime-owned activation gate; never browser or deployment configuration. */
  requireAuthorization?: boolean;
}
type Services = Pick<
  InterfacePluginContext,
  "agent" | "conversations" | "runtimeState"
>;
class GuestHttpError extends Error {
  readonly status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}
const noStore = { "Cache-Control": "no-store", Vary: "Cookie" };

function isLoopbackPeer(address: string | undefined): boolean {
  if (!address) return false;
  if (isIP(address) === 4) return address.startsWith("127.");
  if (isIP(address) !== 6) return false;
  try {
    const host = new URL(`http://[${address}]`).hostname;
    return (
      host === "[::1]" || /^\[::ffff:7f[0-9a-f]{2}:[0-9a-f]{1,4}\]$/.test(host)
    );
  } catch {
    return false;
  }
}

/** A guest presentation of the existing Chat API/runtime, not a second engine. */

/** The HTTP status each admission denial is answered with. */
export const GUEST_DENIAL_STATUS: Record<GuestAdmissionDenial, number> = {
  unavailable: 503,
  "invalid-input": 400,
  "conversation-unavailable": 404,
  "submission-conflict": 409,
  "visitor-busy": 429,
  "deployment-busy": 429,
  "visitor-rate-limit": 429,
  "deployment-rate-limit": 429,
  "conversation-limit": 429,
  "budget-exhausted": 429,
};

/** A visitor's question, validated, with the conversation it belongs to. */
interface GuestSendRequest {
  id: string;
  /** The visitor named the conversation, rather than starting one. */
  explicitId: boolean;
  messageId: string;
  text: string;
}

/** Who owns a guest conversation, and how long it is kept. */
function guestConversationMetadata(
  visitor: GuestVisitor,
  policy: EnabledGuestPolicy,
): { visitorId: string; retention: EnabledGuestPolicy["retention"] } {
  return { visitorId: visitor.id, retention: policy.retention };
}
export class GuestHttpHandlers {
  private readonly policy: GuestPolicy;
  private readonly visitors: GuestVisitorStore;
  private readonly admission: GuestAdmission | undefined;
  private readonly usage: GuestUsageRecord | undefined;
  private readonly now: () => number;
  private readonly ready: () => boolean;
  private readonly services: Services;
  private readonly requireAuthorization: boolean;
  private readonly presentation: GuestHttpOptions["presentation"];
  private readonly subjects: GuestHttpOptions["subjects"];
  constructor(
    services: Services,
    policy: GuestPolicy,
    options: GuestHttpOptions = {},
  ) {
    this.services = services;
    this.presentation = options.presentation;
    this.subjects = options.subjects;
    this.requireAuthorization = options.requireAuthorization === true;
    this.policy = guestPolicySchema.parse(policy);
    this.now = options.now ?? Date.now;
    this.ready = options.ready ?? ((): boolean => false);
    this.visitors = new GuestVisitorStore(
      services.runtimeState,
      this.policy,
      this.now,
    );
    this.admission = this.policy.enabled
      ? new GuestAdmission(services.runtimeState, this.policy, {
          now: this.now,
          isEnabled: this.ready,
          requireAuthorization: this.requireAuthorization,
        })
      : undefined;
    this.usage = this.policy.enabled
      ? new GuestUsageRecord(
          services.runtimeState,
          this.policy.usageRecord,
          this.now,
        )
      : undefined;
  }

  /** Writes counted denials and removes what retention allows; run by guest maintenance. */
  async maintainUsage(): Promise<void> {
    if (!this.usage) return;
    // Neither may starve the other.
    const results = await Promise.allSettled([
      this.usage.flush(),
      this.usage.cleanup(),
    ]);
    if (results.some((result) => result.status === "rejected"))
      throw new Error("Guest usage maintenance unavailable");
  }

  /** The owner's record and its bounds, for the Studio monitor; absent while guest access is off. */
  get usageRecord():
    | {
        record: GuestUsageRecord;
        bounds: EnabledGuestPolicy["usageRecord"];
        /** The record's clock, so its readers count days as it does. */
        now: () => number;
      }
    | undefined {
    return this.usage && this.policy.enabled
      ? { record: this.usage, bounds: this.policy.usageRecord, now: this.now }
      : undefined;
  }

  /** The usage record's bounded health; absent while guest access is off. */
  async usageHealth(): Promise<GuestUsageHealth | undefined> {
    return this.usage?.health();
  }

  routes(apiPath: string): WebRouteDefinition[] {
    const paths = createChatApiPaths(
      `${createChatApiPaths(apiPath).stream}/guest`,
    );
    return [
      this.route(`${paths.stream}/session`, "POST", (request, policy) =>
        this.session(request, policy),
      ),
      this.route(
        paths.stream,
        "POST",
        (request, policy) => this.send(request, policy),
        { recordsRefusals: true },
      ),
      this.route(paths.messages, "GET", (request, policy) =>
        this.history(request, policy),
      ),
      this.route(paths.sessions, "DELETE", (request, policy) =>
        this.delete(request, policy),
      ),
    ];
  }

  private route(
    path: string,
    method: "GET" | "POST" | "DELETE",
    handler: (
      request: Request,
      policy: EnabledGuestPolicy,
    ) => Promise<Response>,
    options: { recordsRefusals?: boolean } = {},
  ): WebRouteDefinition {
    return {
      path,
      method,
      public: true,
      preview: true,
      handler: async (request, transport): Promise<Response> => {
        try {
          if (request.method !== method)
            throw new GuestHttpError(405, "Method not allowed");
          if (!this.policy.enabled)
            throw new GuestHttpError(503, "Guest access unavailable");
          const localOnly = ["localhost", "127.0.0.1", "[::1]"].includes(
            new URL(this.policy.origin).hostname,
          );
          if (localOnly && !isLoopbackPeer(transport?.remoteAddress))
            throw new GuestHttpError(403, "Guest request denied");
          const origin = request.headers.get("origin");
          const served = guestRequestOrigin(request, this.policy);
          if (
            served === undefined ||
            (origin !== null && origin !== served) ||
            request.headers.get("sec-fetch-site") === "cross-site" ||
            (method !== "GET" && origin !== served)
          )
            throw new GuestHttpError(403, "Guest request denied");
          if (
            method === "POST" &&
            request.headers
              .get("content-type")
              ?.split(";")[0]
              ?.trim()
              .toLowerCase() !== "application/json"
          )
            throw new GuestHttpError(415, "JSON required");
          request.signal.throwIfAborted();
          const response = await handler(request, this.policy);
          for (const [key, value] of Object.entries(noStore))
            response.headers.set(key, value);
          return response;
        } catch (error) {
          // A refused question is recorded by category; its body never is.
          if (options.recordsRefusals && error instanceof GuestHttpError)
            await this.usage?.deny(refusal(error.status), undefined);
          // Only locally defined errors cross HTTP; provider/storage details do not.
          return Response.json(
            {
              error:
                error instanceof GuestHttpError
                  ? error.message
                  : "Guest access unavailable",
            },
            {
              status: error instanceof GuestHttpError ? error.status : 503,
              headers: noStore,
            },
          );
        }
      },
    };
  }

  private async session(
    request: Request,
    policy: EnabledGuestPolicy,
  ): Promise<Response> {
    const body = await this.body(request, policy);
    if (!z.strictObject({}).safeParse(body).success)
      throw new GuestHttpError(400, "Invalid guest session request");
    const canSend =
      this.ready() &&
      (!this.requireAuthorization ||
        (await this.admission?.accessStatus())?.enabled === true);
    let visitor = await this.visitors.resolve(request);
    let cookie: string | undefined;
    if (!visitor) {
      if (!canSend) throw new GuestHttpError(503, "Guest access unavailable");
      const issued = await this.visitors.issue(request);
      visitor = issued.visitor;
      cookie = issued.cookie;
    }
    // Refresh reuses the credential without renewing its fixed lifetime.
    const response = Response.json(
      guestChatSessionResponseSchema.parse({
        expiresAt: visitor.expiresAt,
        ...policy.disclosure,
        retention: policy.retention,
        messageCharacters: policy.limits.messageCharacters,
        canSend,
        presentation: await this.presentation?.(),
      }),
    );
    if (cookie) response.headers.set("Set-Cookie", cookie);
    return response;
  }

  private async owner(request: Request): Promise<GuestVisitor> {
    const visitor = await this.visitors.resolve(request);
    if (!visitor) throw new GuestHttpError(404, "Conversation unavailable");
    return visitor;
  }

  private async owned(
    request: Request,
    id: string,
    policy: EnabledGuestPolicy,
  ): Promise<WebChatConversation> {
    const visitor = await this.owner(request);
    const conversation = await this.services.conversations.get(id);
    if (!canAccessGuestConversation(conversation, visitor, policy, this.now()))
      throw new GuestHttpError(404, "Conversation unavailable");
    return conversation;
  }

  private async send(
    request: Request,
    policy: EnabledGuestPolicy,
  ): Promise<Response> {
    if (!this.ready() || !this.admission || !this.usage)
      throw new GuestHttpError(503, "Guest access unavailable");
    const visitor = await this.owner(request);
    const { id, explicitId, messageId, text } = await this.parseSend(
      request,
      policy,
      visitor,
    );
    const existing = await this.services.conversations.get(id);
    if (explicitId || existing) await this.owned(request, id, policy);
    const timestamp = new Date(this.now()).toISOString();
    const candidate: WebChatConversation = existing ?? {
      id,
      sessionId: id,
      interfaceType: guestInterfaceType,
      channelId: id,
      startedAt: timestamp,
      lastActiveAt: timestamp,
      createdAt: timestamp,
      updatedAt: timestamp,
      metadata: { guest: guestConversationMetadata(visitor, policy) },
    };
    // The owner's record takes this request's place before admission is asked:
    // work nobody can see is refused, without spending allowance.
    const usage = this.usage;
    const usageId = GuestUsageRecord.id(
      policy.origin,
      visitor.id,
      candidate.id,
      messageId,
    );
    const opening = await usage.open(usageId);
    if (opening === "full" || opening === "unavailable") {
      await usage.deny(
        opening === "full" ? "record-full" : "record-unavailable",
        visitor.id,
      );
      return Response.json({ error: "unavailable" }, { status: 503 });
    }
    // A new conversation consumes a real admission reservation before any write.
    const reservation = await this.admission.reserve(
      visitor,
      candidate,
      messageId,
      text,
    );
    if (reservation.kind !== "reserved" && opening === "opened")
      await usage.withdraw(usageId);
    if (reservation.kind === "denied") {
      await usage.deny(reservation.reason, visitor.id);
      return Response.json(
        { error: reservation.reason },
        { status: GUEST_DENIAL_STATUS[reservation.reason] },
      );
    }
    if (reservation.kind === "duplicate")
      return Response.json(
        guestChatSubmissionStatusSchema.parse({
          state: reservation.state,
          conversationId: id,
        }),
        { status: 409, headers: { [CHAT_CONVERSATION_ID_HEADER]: id } },
      );
    if (
      !(await usage.admit(usageId, {
        visitorId: visitor.id,
        reservedMicroUsd: reservation.lease.execution.maxCostMicroUsd,
        question: text,
      }))
    ) {
      // Unrecorded work never runs. Nothing ran, so the reservation settles as failed.
      await this.admission.settle(reservation.lease, "failed");
      await usage.deny("record-unavailable", visitor.id);
      return Response.json({ error: "unavailable" }, { status: 503 });
    }
    if (!existing)
      await this.services.conversations.start({
        sessionId: id,
        interfaceType: guestInterfaceType,
        channelId: id,
        metadata: {
          channelName: "Public Ask",
          interfaceType: guestInterfaceType,
          channelId: id,
          guest: guestConversationMetadata(visitor, policy),
        },
      });
    await this.owned(request, id, policy);
    return this.streamAnswer(request, policy, {
      id,
      text,
      usage,
      usageId,
      admission: this.admission,
      lease: reservation.lease,
    });
  }

  /** The visitor's question and the conversation it belongs to, or a 400. */
  private async parseSend(
    request: Request,
    policy: EnabledGuestPolicy,
    visitor: GuestVisitor,
  ): Promise<GuestSendRequest> {
    const parsed = guestChatMessageRequestSchema.safeParse(
      await this.body(request, policy),
    );
    if (!parsed.success) throw new GuestHttpError(400, "Invalid guest message");
    const message = parsed.data.messages[0];
    if (!message) throw new GuestHttpError(400, "Invalid guest message");
    const text = message.parts.map((part) => part.text).join("\n");
    if (!text.trim() || text.length > policy.limits.messageCharacters)
      throw new GuestHttpError(400, "Invalid guest message");
    // Stable across retries of a first send, but minted using server-owned identity.
    const id =
      parsed.data.id ??
      `guest-${createHash("sha256")
        .update(JSON.stringify([visitor.id, message.id]))
        .digest("hex")}`;
    if (/[\r\n]/u.test(id))
      throw new GuestHttpError(400, "Invalid guest message");
    return {
      id,
      explicitId: parsed.data.id !== undefined,
      messageId: message.id,
      text,
    };
  }

  /**
   * Runs an admitted question and streams its answer. Both ledgers are
   * settled when the model call returns, answered or not; a visitor who stops
   * waiting does not stop that settlement.
   */
  private streamAnswer(
    request: Request,
    policy: EnabledGuestPolicy,
    turn: {
      id: string;
      text: string;
      usage: GuestUsageRecord;
      usageId: string;
      admission: GuestAdmission;
      lease: GuestExecutionLease;
    },
  ): Response {
    const { id, text, usage, usageId, admission, lease } = turn;
    const stream = createUIMessageStream<UIMessage>({
      onError: (): string => "Guest response unavailable",
      execute: async ({ writer }): Promise<void> => {
        // Do not invoke the runtime if creation waited past expiry/deletion.
        await this.owned(request, id, policy);
        if (!this.ready()) throw new Error("Guest access unavailable");
        request.signal.throwIfAborted();
        writer.write({ type: "start", messageId: randomUUID() });
        // One answer batch, as in owner chat: the turn's own deadline bounds
        // the wait, and a visitor who leaves stops only the delivery.
        const signal = request.signal;
        const stopped = deferred<never>();
        const stopWaiting = (): void =>
          stopped.reject(new Error("Guest response unavailable"));
        signal.addEventListener("abort", stopWaiting, { once: true });
        try {
          signal.throwIfAborted();
          // Keep observing work after delivery stops: the model call settles
          // the answer when it returns, answered or not.
          const work = Promise.resolve().then(async () => {
            // The site's topics bound what the question is screened against,
            // and its refusal line is what a screened-out visitor reads.
            // The question is screened against the brain's topics and the
            // owner's introduction; a screened-out visitor reads the refusal.
            const [content, topics] = await Promise.all([
              this.presentation?.(),
              this.subjects?.() ?? [],
            ]);
            signal.throwIfAborted();
            const response = await this.services.agent
              .chat(
                text,
                id,
                {
                  interfaceType: guestInterfaceType,
                  userPermissionLevel: "public",
                  isAnchor: false,
                  guestExecution: lease.execution,
                  guestScreening: {
                    topics,
                    ...(content?.introduction
                      ? { introduction: content.introduction }
                      : {}),
                    ...(content?.refusal ? { refusal: content.refusal } : {}),
                  },
                },
                signal,
              )
              .catch(async (error: unknown) => {
                // The call has returned, so its work has ended: settle it at the
                // answer cap, since a failed call reports no usage to measure.
                const outcome = signal.aborted ? "interrupted" : "failed";
                await usage.settle(usageId, "failed", undefined);
                await admission.settle(lease, outcome);
                throw error;
              });
            // A failed turn the runtime still words for the visitor (a
            // provider decline) settles as failed; its text is still shown.
            const hasText = response.text.trim().length > 0;
            const hasAnswer = hasText && response.error === undefined;
            // The owner's record first: if it cannot be written, the request
            // stays unresolved and its reservation held.
            if (
              !(await usage.settle(
                usageId,
                hasAnswer ? "completed" : "failed",
                response.guestSettlement,
                response.guestScreening,
              ))
            )
              throw new Error("Guest settlement unavailable");
            // A measured cost returns the rest of the quote; unknown cost keeps it.
            const cost = response.guestSettlement?.cost;
            if (
              !(await admission.settle(
                lease,
                hasAnswer ? "completed" : "failed",
                cost?.state === "known" ? cost.microUsd : undefined,
              ))
            )
              throw new Error("Guest settlement unavailable");
            if (!hasText) throw new Error("Guest response unavailable");
            signal.throwIfAborted();
            await this.owned(request, id, policy);
            signal.throwIfAborted();
            return response;
          });
          const response = await Promise.race([work, stopped.promise]);
          signal.throwIfAborted();
          // Only bounded source projections cross this boundary; never actions,
          // approvals, attachments or raw provenance from the runtime.
          for (const card of getGuestSourceCards(response.cards)) {
            writer.write({ type: "data-sources", id: card.id, data: card });
          }
          // A FAQ answered in the model's place: the box says so by the answer.
          if (response.askedBefore) {
            writer.write({
              type: "data-asked-before",
              id: "asked-before",
              data: { faqId: response.askedBefore.faqId },
            });
          }
          writeTextPart(writer, randomUUID(), response.text);
          // A failed turn's words (a provider decline) finish as an error, so
          // the box never presents them as an answer.
          writer.write({
            type: "finish",
            finishReason: response.error === undefined ? "stop" : "error",
          });
        } finally {
          signal.removeEventListener("abort", stopWaiting);
        }
      },
    });
    return createUIMessageStreamResponse({
      stream,
      headers: { ...noStore, [CHAT_CONVERSATION_ID_HEADER]: id },
    });
  }

  private async history(
    request: Request,
    policy: EnabledGuestPolicy,
  ): Promise<Response> {
    const id = this.conversationId(request);
    const conversation = await this.owned(request, id, policy);
    const submissionId = new URL(request.url).searchParams.get("submissionId");
    const parsed =
      submissionId === null
        ? undefined
        : guestChatSubmissionIdSchema.safeParse(submissionId);
    if (parsed && !parsed.success)
      throw new GuestHttpError(400, "Invalid guest submission");
    const state = parsed?.success
      ? await this.admission?.status(
          await this.owner(request),
          conversation,
          parsed.data,
        )
      : undefined;
    const messages = await this.services.conversations.getMessages(id, {
      limit: Math.min(100, policy.limits.userTurns * 2),
    });
    await this.owned(request, id, policy);
    const history = chatMessagesResponseSchema.parse({
      messages: messages.map((message) => {
        const askedBefore = askedBeforeSchema.safeParse(
          message.metadata["askedBefore"],
        );
        return {
          id: message.id,
          role: message.role,
          content: message.content,
          cards: getGuestSourceCards(message.metadata["cards"]),
          ...(askedBefore.success ? { askedBefore: askedBefore.data } : {}),
        };
      }),
    });
    return Response.json(
      submissionId === null
        ? history
        : guestChatHistoryResponseSchema.parse({
            messages: history.messages,
            submission: state ? { state, conversationId: id } : null,
          }),
    );
  }

  private async delete(
    request: Request,
    policy: EnabledGuestPolicy,
  ): Promise<Response> {
    const id = this.conversationId(request);
    await this.owned(request, id, policy);
    const deleted = await this.services.conversations.delete(id);
    // Deletion does not cancel or release an active execution reservation.
    return Response.json(deleteChatSessionResponseSchema.parse({ deleted }));
  }

  private conversationId(request: Request): string {
    const id = new URL(request.url).searchParams.get("id");
    if (!id || id.length > 256)
      throw new GuestHttpError(400, "Missing conversation id");
    return id;
  }

  private async body(
    request: Request,
    policy: EnabledGuestPolicy,
  ): Promise<unknown> {
    if (!request.body) throw new GuestHttpError(400, "Invalid JSON body");
    const reader = request.body.getReader();
    const signal = AbortSignal.any([
      request.signal,
      AbortSignal.timeout(policy.limits.requestTimeoutSeconds * 1000),
    ]);
    const cancel = (): void => {
      void reader.cancel().catch(() => {
        // Body cancellation is best-effort, never evidence of runtime completion.
      });
    };
    signal.addEventListener("abort", cancel, { once: true });
    const drain = async (
      chunks: Uint8Array[],
      size: number,
    ): Promise<Uint8Array[]> => {
      const result = await reader.read();
      signal.throwIfAborted();
      if (result.done) return chunks;
      const total = size + result.value.byteLength;
      // One message of at most messageCharacters (4 UTF-8 bytes each) plus its envelope.
      if (total > policy.limits.messageCharacters * 4 + 4096) {
        cancel();
        throw new GuestHttpError(413, "Guest request too large");
      }
      return drain([...chunks, result.value], total);
    };
    try {
      signal.throwIfAborted();
      const bytes = Buffer.concat(await drain([], 0));
      try {
        return JSON.parse(
          new TextDecoder("utf-8", { fatal: true }).decode(bytes),
        );
      } catch {
        throw new GuestHttpError(400, "Invalid JSON body");
      }
    } finally {
      signal.removeEventListener("abort", cancel);
      reader.releaseLock();
    }
  }
}
